import { useState, useEffect } from "react";
import * as Yup from "yup";
// form
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
// @mui
import { Box, Stack, Alert, IconButton, InputAdornment, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
// routes
// auth
import { useAuthContext } from "../../auth/useAuthContext";
// components
import Iconify from "../../components/iconify";
import FormProvider, { RHFTextField } from "../../components/hook-form";
import { getLocalIPs } from "../../utils/getLocalIPs";
import { gatherBrowserInfo } from "../../utils/gatherBrowserInfo"
import WarehouseSelectionModal from "./WarehouseSelectionModal";

// ----------------------------------------------------------------------

export default function AuthLoginForm() {
  const [localIPs, setLocalIPs] = useState([]);
  const [browserInfo, setBrowserInfo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const browserInformation = await gatherBrowserInfo();
        setBrowserInfo(browserInformation);

        const ip = await getLocalIPs();
        setLocalIPs(ip);
      } catch (error) {
        console.error('Error fetching info:', error);
      }
    };

    fetchData();
  }, []);

  const { login, requiresWarehouseSelection, cancelWarehouseSelection } = useAuthContext();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    userName: Yup.string().required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const defaultValues = {
    userName: "",
    password: "",
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const handleSelectionExpired = () => {
    cancelWarehouseSelection();
    reset();
    setError("afterSubmit", { message: "Warehouse selection expired. Please log in again." });
  };

  const onSubmit = async (data) => {
    try {
      await login(data.userName, data.password, localIPs[0], browserInfo);
    } catch (error) {
      console.error(error);
      reset();
      setError("afterSubmit", {
        ...error,
        message: error.message,
      });
    }
  };

  return (
    <>
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3.5}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>
            Welcome back
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Log in to run your warehouse.
          </Typography>
        </Box>

        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <Stack spacing={2.5}>
          <RHFTextField
            name="userName"
            label="Email"
            autoComplete="username"
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:email-outline" width={20} sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
          />

          <RHFTextField
            name="password"
            label="Password"
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:lock-outline" width={20} sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <Iconify icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <LoadingButton
          fullWidth
          size="large"
          type="submit"
          variant="contained"
          color="primary"
          loading={isSubmitSuccessful || isSubmitting}
          endIcon={<Iconify icon="eva:arrow-forward-fill" />}
          sx={(theme) => ({
            py: 1.6,
            fontSize: 16,
            fontWeight: 700,
            boxShadow: theme.customShadows?.primary,
            transition: theme.transitions.create(["transform", "box-shadow"], { duration: 200 }),
            "&:hover": { transform: "translateY(-1px)" },
          })}
        >
          Login
        </LoadingButton>
      </Stack>
    </FormProvider>

    <WarehouseSelectionModal
      open={requiresWarehouseSelection}
      mode="login"
      onCancel={handleSelectionExpired}
    />
    </>
  );
}
